import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { Components } from '@ionic/core';
import { addIcons } from 'ionicons';
import { chevronDownCircleOutline, closeCircleOutline, fileTrayOutline, openOutline, removeCircleOutline } from 'ionicons/icons';
import { CloudFunctionsService } from 'src/services/cloudfunctions.service';
import { FirestoreService } from 'src/services/firestore.service';
import { UIService } from 'src/services/ui.service';
import { Transaction } from 'bankapp-types';
import { ModalController } from '@ionic/angular/standalone';
import { ReconcileTransactionComponent } from '../reconcile-transaction/reconcile-transaction.component';
import { CurrencyPipe } from '@angular/common';

@Component({
    selector: 'app-reconcile',
    templateUrl: 'reconcile.component.html',
    styleUrls: ['reconcile.component.scss']
})
export class ReconcileComponent {


    @Input() modal: Components.IonModal;

    checked = []

    expanded = []

    investorRecon = []

    reconcileType: string

    reconcileTo: string

    reconcileOptions = [
        "Investor",
        "Investor (out)",
        "Borrower",
        "Other"
    ]

    slideOffset: number = 0

    constructor(
        public firestoreService: FirestoreService,
        private cloud: CloudFunctionsService,
        private ui: UIService,
        private modalController: ModalController,
        private currencyPipe: CurrencyPipe
    ) {
        addIcons({ openOutline, chevronDownCircleOutline, closeCircleOutline, removeCircleOutline });
    }


    async reconcile() {
        if(!this.reconcileType) return

        
        if(this.reconcileType !== 'Investor') {
            if(!this.reconcileTo) return

            const loading = await this.ui.presentLoading("Reconciling...")

            for(let i of this.checked) {
                const transaction = this.firestoreService.paginatedTransactions[i]
                await this.firestoreService.updateTransaction(this.firestoreService.currentBank.bankId, transaction.id, { 
                    tags: [...new Set([...transaction.tags ?? [], this.reconcileType == "Investor (out)" ? "Investor" : this.reconcileType])],
                    recon: this.reconcileTo
                })
            }

            await loading.dismiss()
        } else {

            const loading = await this.ui.presentLoading("Reconciling...")

            let oldTransactions = []

            let newTransactions = []

            let updates = []

            for(let i = 0; i < this.checked.length; i++) {
                if(this.expanded[i] && !this.investorRecon[i].length) {
                    await loading.dismiss()
                    await this.ui.presentOKAlert("Error", "Please reconcile all transactions before submitting")
                    return
                }

                const transaction = this.firestoreService.paginatedTransactions[this.checked[i]]
                oldTransactions.push(transaction)

                await this.firestoreService.deleteTransaction(this.firestoreService.currentBank.bankId, transaction.id, undefined, transaction.index)

                let runningBalance = 0
                
                for(let j = 0; j < this.investorRecon[i].length; j++) {
                    const recon = this.investorRecon[i][j]
                    runningBalance += recon.amount

                    if(recon.dealName && recon.path) {
                        const updateData = {
                            dealName: recon.dealName,
                            path: recon.path,
                            index: recon.index,
                            entityId: recon.entityId,
                        }

                        newTransactions.push(new Transaction({
                            ...transaction.toJson(),
                            transaction: recon.amount,
                            recon: this.getShortName(recon.dealName),
                            tags: [...new Set([...transaction.tags ?? [], this.reconcileType])],
                            index: transaction.index + j / 1000,
                            bankBalance: transaction.bankBalance - transaction.transaction + runningBalance,
                            runningBalance: transaction.runningBalance - transaction.transaction + runningBalance,
                            update: updateData
                        }))
    
                        updates.push({
                            ...updateData,
                            date: transaction.date,
                            amount: recon.amount
                        })
                    } else {
                        newTransactions.push(new Transaction({
                            ...transaction.toJson(),
                            transaction: recon.amount,
                            bankBalance: transaction.bankBalance - transaction.transaction + runningBalance,
                            runningBalance: transaction.runningBalance - transaction.transaction + runningBalance,
                        }))
                    }
                }
            }

            const minIndex = Math.min(...oldTransactions.map(x => x.index));

            const existingTransactions = await this.firestoreService.getTransactionsAboveIndex(minIndex)

            const reindexedTransactions = [...existingTransactions?.filter(x => !oldTransactions.find(t => t.id === x.id)), ...newTransactions].sort((a, b) => a.index - b.index).map((x, i, arr) => new Transaction({
                ...x, 
                index: minIndex + i,
            }))

            await Promise.all(reindexedTransactions.map(async transaction => {
                if(transaction.id) {
                    await this.firestoreService.updateTransaction(this.firestoreService.currentBank.bankId, transaction.id, transaction.toJson())
                }  else {
                    console.log(transaction, transaction.toJson())
                    await this.firestoreService.addTransaction(this.firestoreService.currentBank.bankId, transaction.toJson())
                }
            }))

            this.firestoreService.currentBank[this.firestoreService.viewBpay ? 'bpayTransactionCounter' : 'transactionCounter'] = this.firestoreService.currentBank[this.firestoreService.viewBpay ? 'bpayTransactionCounter' : 'transactionCounter'] + newTransactions.length - oldTransactions.length

            await this.firestoreService.updateBankAccount(this.firestoreService.currentBank.bankId, {
                [this.firestoreService.viewBpay ? 'bpayTransactionCounter' : 'transactionCounter']: this.firestoreService.currentBank[this.firestoreService.viewBpay ? 'bpayTransactionCounter' : 'transactionCounter']
            })

            const uniquePaths = [...new Set(updates.map((x: any) => x.path))] as string[]

            for(let path of uniquePaths) {
                const relevantUpdates = updates.filter((x: any) => x.path === path)
                const updateBatch = {
                    path,
                    dealName: relevantUpdates[0].dealName ?? "",
                    status: "toRun",
                    updates: updates.filter((x: any) => x.path === path).reduce((acc, curr) => {
                        const existing = acc.find(x => x.entityId === curr.entityId && x.path === curr.path && x.index === curr.index);
                        if (existing) {
                            existing.amount += curr.amount;
                            existing.date = new Date(Math.max(new Date(existing.date).getTime(), new Date(curr.date).getTime())).getTime();
                        } else {
                            acc.push(curr);
                        }
                        return acc;
                    }, []),
                }
                await this.firestoreService.addOCLUpdate(updateBatch)
            }

            await loading.dismiss()

        }

        await this.modal.dismiss(true)
    }

    async reconcileTransaction(transaction: Transaction, index: number) {
        const investorRecon = this.investorRecon.flat()
        const modal = await this.modalController.create({
            component: ReconcileTransactionComponent,
            componentProps: {
                transaction,
                investorRecon
            },
            cssClass: 'big-modal'
        })

        await modal.present()

        const { data } = await modal.onDidDismiss()

        if(data?.length) {
            const totalCalled = data.reduce((acc, curr) => acc + curr.called, 0)
            const totalReceived = data.reduce((acc, curr) => acc + curr.received + investorRecon?.filter(x => x?.dealName === curr?.dealName && x?.index === curr?.index && x?.entityId === curr?.entityId).reduce((a,b) => a + b.amount, 0), 0)
            let unfunded =  totalCalled - totalReceived
            let excess = 0
            const excessTransaction = []
            if(transaction.transaction > unfunded) {
                excess = transaction.transaction - Math.max(unfunded, 0)
                this.ui.presentOKAlert("Error", `The reconciled amount exceeds the total called amount. There will be an unreconciled transaction in the amount of ${this.currencyPipe.transform(excess)}`)
                excessTransaction.push({
                    date: transaction.date,
                    amount: excess,
                })
            }

            this.investorRecon[index] = [...excessTransaction, ...data.map(x => {
                const unfund = x.called - x.received - investorRecon?.filter(y => y?.dealName === x?.dealName && y?.index === x?.index && y?.entityId === x?.entityId).reduce((a,b) => a + b.amount, 0)
                return {
                    date: x.date,
                    amount: (transaction.transaction - excess) * unfund / unfunded,
                    dealName: x.dealName,
                    path: x.path,
                    index: x.index,
                    entityId: x.entityId
                }
            })].filter(x => x.amount > 0)
            console.log(this.investorRecon)
        }
        this.investorRecon = [...this.investorRecon]
    }

    async resetRecon(index: number) {
        if(!(await this.ui.presentYesNoAlert("Reset Reconciliation", "Are you sure you want to reset this reconciliation?"))) return

        this.expanded[index] = false
        this.investorRecon[index] = []
    }

    async removeRecon(index: number) {
        const proceed = await this.ui.presentYesNoAlert("Remove Reconciliation", "Are you sure you want to remove this reconciliation?")
        if(!proceed) return
        this.investorRecon = this.investorRecon.filter((_, i) => i !== index)
        this.checked = this.checked.filter((_, i) => i !== index)
        this.expanded = this.expanded.filter((_, i) => i !== index)
    }

    getShortName(name: string) {
        if(name === "FST161M") return "FST161"

        const shortNames = ["FST", "SUB", "DIR"]
      
        if(shortNames.includes(name.substring(0,3))) return name
      
        if(name.includes("First Mortgage")) {
          return "FST" + name.match(/First Mortgage ([0-9]+)/)?.[1]
        } else if(name.includes("Syndicate")) {
          return "SUB" + name.match(/Syndicate ([0-9]+)/)?.[1]
        } else {
          return name
        }
    }

    dismissModal() {
        this.modal.dismiss()
    }

}
